import { type EntityStatus } from '@core';
import { type IncidentSeverity } from '@models';

import { type FloorSpaceModel, type RoomSpaceModel } from './spaces';

export interface LocationBaseModel {
  address: {
    city: string;
    country: string;
    postalCode: string;
    stateProvince: string;
    streetAddress: string;
    streetAddress2: string;
  };
  clientFriendlyId: string;
  friendlyName: string;
  geographic: {
    lat: number;
    long: number;
  };
  name: string;
  status: EntityStatus;
}

export interface LocationLogsData {
  action: string;
  id: string;
  location: string;
  updated: string;
  user: string;
}

export interface DeviceLocation {
  floorId: FloorSpaceModel['id'];
  roomId: RoomSpaceModel['id'];
  siteId: LocationData['id'];
}

export interface SpaceStats {
  criticalAndMajorIncidents: number;
  deviceActive: number;
  devicePaused: number;
  floors: number;
  minorIncidents: number;
  rooms: number;
}

export const initialStats = (): SpaceStats => {
  return {
    criticalAndMajorIncidents: 0,
    deviceActive: 0,
    devicePaused: 0,
    floors: 0,
    minorIncidents: 0,
    rooms: 0,
  };
};

export interface WidgetModel {
  building: {
    id: string;
    value: string;
  };
  deviceAlert: number;
  deviceGood: number;
  deviceOffline: number;
  devices: number;
  deviceWarning: number;
  floor?: {
    id: string;
    value: string;
  };
  id: string;
  incidents: number;
  name: string;
  room?: {
    id: string;
    value: string;
  };
}

export interface WidgetCreationBase {
  buildingId: string;
  floorId?: string;
  name: string;
  roomId?: string;
}

export interface LocationData {
  address: LocationAddress;
  clientFriendlyId: string;
  devicesByStatuses?: {
    activeCount: number;
    deactivatedCount: number;
    pausedCount: number;
  };
  devicesCount?: number;
  friendlyName: string;
  geographic: {
    lat: number;
    long: number;
  };
  id: string;

  importantDates?: {
    comissioningInfo: {
      dateTime: string;
    };
    decomissionedInfo: {
      dateTime: string;
    };
    installationInfo: {
      dateTime: string;
    };
    provisioningInfo: {
      dateTime: string;
    };
  };
  incidentCountBySeverity: Record<IncidentSeverity, number>;
  name: string;
  status: EntityStatus;
  tags: {
    [key: string]: string;
  };
}

export interface EntitySummary {
  devicesByStatuses: {
    activeCount: number;
    deactivatedCount: number;
    pausedCount: number;
  };
  devicesCount: number;
  incidentCountBySeverity: Record<IncidentSeverity, number>;
}

export interface LocationAddress {
  city: string;
  country: string;
  postalCode: string;
  stateProvince: string;
  streetAddress: string;
  streetAddress2: string;
}

export interface LocationFullData extends LocationData {
  floors: FloorSpaceModel[];
}
